import i18n from '@/utils/i18n';
import React from 'react';
import {
  EmptyState,
  Icons,
  SectionHeading,
  Skeleton,
  Surface,
  Typography,
  XStack,
  YStack,
} from '@usepepper/sprout-kit-ui';
import { getProspectStageVisual } from '@/experiences/sales-portal/domain/prospects/blocs/utils/prospect-stage-visuals';
import {
  MANAGER_PIPELINE_BAR_MAX_HEIGHT,
  MANAGER_PIPELINE_BAR_MIN_HEIGHT,
  MANAGER_PIPELINE_COLUMN_HEIGHT,
  MANAGER_PIPELINE_SKELETON_COLUMNS,
  MANAGER_PIPELINE_STAGE_LABEL_HEIGHT,
} from '@/experiences/sales-portal/domain/prospects/screens/prospects/constants';
import { ManagerSectionError } from '@/experiences/sales-portal/domain/prospects/screens/prospects/components/prospects-manager-view/manager-section-error';
import { useTheme } from '@/contexts/theme-context';
import type { ColorTokens } from '@usepepper/sprout-kit-ui';
import type { ManagerPipelineStageData } from '@/experiences/sales-portal/domain/prospects/screens/prospects/types';

const EMPTY_ICON_SIZE = 20;
const STAGE_MIN_WIDTH = 96;
const STAGE_ICON_SIZE = 9;
const SKELETON_COUNT_WIDTH = 24;
const SKELETON_LABEL_WIDTH = 64;
const SKELETON_TEXT_HEIGHT = 12;
const SKELETON_VALUE_WIDTH = 44;

const EMPTY_BAR_COLOR: ColorTokens = '$bgRailDefault';

const TABULAR_NUMS = { fontVariantNumeric: 'tabular-nums' } as const;

type StackGap = React.ComponentProps<typeof YStack>['gap'];

interface PipelineCardProps {
  children: React.ReactNode;
  gap?: StackGap;
}

interface ProspectsManagerPipelineSummaryProps {
  hasError: boolean;
  isLoading: boolean;
  onRetry: () => void;
  stages: ManagerPipelineStageData[];
}

const toBarHeight = (
  valueInMicros: number,
  maxValueInMicros: number,
): number =>
  maxValueInMicros > 0
    ? Math.max(
        MANAGER_PIPELINE_BAR_MIN_HEIGHT,
        Math.round(
          (valueInMicros / maxValueInMicros) * MANAGER_PIPELINE_BAR_MAX_HEIGHT,
        ),
      )
    : MANAGER_PIPELINE_BAR_MIN_HEIGHT;

const PipelineCard: React.FC<PipelineCardProps> = ({
  children,
  gap = '$16',
}) => (
  <Surface>
    <YStack gap={gap} padding="$16" paddingTop="$0">
      <SectionHeading
        size="$md"
        title={i18n.t('sales_portal.prospects.manager.pipeline.heading')}
      />

      {children}
    </YStack>
  </Surface>
);

export const ProspectsManagerPipelineSummary: React.FC<
  ProspectsManagerPipelineSummaryProps
> = ({ hasError, isLoading, onRetry, stages }) => {
  const theme = useTheme();

  if (hasError) {
    return (
      <PipelineCard gap="$8">
        <ManagerSectionError onRetry={onRetry} />
      </PipelineCard>
    );
  }

  if (isLoading) {
    return (
      <PipelineCard>
        <XStack
          accessibilityLabel={i18n.t('sales_portal.prospects.loading')}
          accessibilityRole="progressbar"
          accessibilityState={{ busy: true }}
          flexWrap="wrap"
          gap="$12"
        >
          {Array.from({ length: MANAGER_PIPELINE_SKELETON_COLUMNS }).map(
            (_, index) => (
              <YStack flex={1} gap="$8" key={index} minWidth={STAGE_MIN_WIDTH}>
                <YStack
                  gap="$8"
                  height={MANAGER_PIPELINE_COLUMN_HEIGHT}
                  justifyContent="flex-end"
                >
                  <YStack gap="$2">
                    <Skeleton
                      height={SKELETON_TEXT_HEIGHT}
                      loadingLabel=""
                      width={SKELETON_COUNT_WIDTH}
                    />
                    <Skeleton
                      height={SKELETON_TEXT_HEIGHT}
                      loadingLabel=""
                      width={SKELETON_VALUE_WIDTH}
                    />
                  </YStack>
                  <Skeleton
                    borderRadius={theme.sk.radii.radiusMedium}
                    height={MANAGER_PIPELINE_BAR_MAX_HEIGHT}
                    loadingLabel=""
                    width="100%"
                  />
                </YStack>

                <XStack
                  alignItems="center"
                  height={MANAGER_PIPELINE_STAGE_LABEL_HEIGHT}
                >
                  <Skeleton
                    height={SKELETON_TEXT_HEIGHT}
                    loadingLabel=""
                    width={SKELETON_LABEL_WIDTH}
                  />
                </XStack>
              </YStack>
            ),
          )}
        </XStack>
      </PipelineCard>
    );
  }

  if (stages.every((stage) => stage.prospectCount === 0)) {
    return (
      <PipelineCard>
        <YStack paddingVertical="$24" width="100%">
          <EmptyState
            description={i18n.t(
              'sales_portal.prospects.manager.pipeline.empty_description',
            )}
            icon={<Icons.Filter color="$textTertiary" size={EMPTY_ICON_SIZE} />}
            title={i18n.t(
              'sales_portal.prospects.manager.pipeline.empty_title',
            )}
          />
        </YStack>
      </PipelineCard>
    );
  }

  const maxValueInMicros = Math.max(
    ...stages.map((stage) => stage.totalValueInMicros),
    0,
  );

  return (
    <PipelineCard>
      <XStack flexWrap="wrap" gap="$12">
        {stages.map((stage) => {
          const {
            color: stageColor,
            icon: StageIcon,
            isHollow,
          } = getProspectStageVisual(stage.stage);
          const iconColor = theme.sk.color.text.secondary;

          return (
            <YStack
              accessibilityLabel={stage.stageLabel}
              flex={1}
              gap="$8"
              key={stage.stage}
              minWidth={STAGE_MIN_WIDTH}
              role="group"
            >
              <YStack
                gap="$8"
                height={MANAGER_PIPELINE_COLUMN_HEIGHT}
                justifyContent="flex-end"
              >
                <YStack gap="$2">
                  <Typography
                    accessibilityLabel={stage.prospectCountLabel}
                    color={
                      stage.prospectCount === 0
                        ? '$textTertiary'
                        : '$textPrimary'
                    }
                    fontWeight="$medium"
                    size="$sm"
                    style={TABULAR_NUMS}
                    variant="body"
                  >
                    {stage.prospectCountDisplay}
                  </Typography>

                  <Typography
                    color="$textTertiary"
                    size="$xs"
                    style={TABULAR_NUMS}
                    variant="caption"
                  >
                    {stage.totalValueDisplay}
                  </Typography>
                </YStack>

                <YStack
                  backgroundColor={
                    stage.totalValueInMicros > 0 ? stageColor : EMPTY_BAR_COLOR
                  }
                  borderBottomLeftRadius="$0"
                  borderBottomRightRadius="$0"
                  borderTopLeftRadius="$8"
                  borderTopRightRadius="$8"
                  height={toBarHeight(
                    stage.totalValueInMicros,
                    maxValueInMicros,
                  )}
                />
              </YStack>

              <XStack
                alignItems="center"
                gap="$4"
                height={MANAGER_PIPELINE_STAGE_LABEL_HEIGHT}
              >
                <StageIcon
                  color={iconColor}
                  fill={isHollow ? 'transparent' : iconColor}
                  size={STAGE_ICON_SIZE}
                />
                <Typography color="$textSecondary" size="$xs" variant="caption">
                  {stage.stageLabel}
                </Typography>
              </XStack>
            </YStack>
          );
        })}
      </XStack>
    </PipelineCard>
  );
};
